import {useState, useCallback} from 'react';
import {toast} from 'sonner';
import {type FeedbackCategory} from '@features/db/feedbackAttachment';
import {trpc} from '@features/utils/trpc';
import {useTriggerGeneration} from '@features/ai/streaming/useGeneration';
import amplitudeService from '@features/analytics/amplitudeService';
import {captureException} from '@sentry/react';

type UseFeedbackParams = {
  flowId: string;
  screenId: string | null;
  structureId: string | undefined;
  analyticsSource: 'Canvas' | 'Versions';
  screenshotUrl: string | null | undefined;
};

type FeedbackAction = 'like' | 'dislike' | null;

export const useFeedback = ({
  flowId,
  screenId,
  structureId,
  analyticsSource,
  screenshotUrl,
}: UseFeedbackParams) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<FeedbackAction>(null);

  const sendFeedbackToSlackMutation =
    trpc.likes.sendFeedbackToSlack.useMutation();
  const pushRegenerateMessageMutation =
    trpc.generation.pushRegenerateMessage.useMutation();
  const triggerGeneration = useTriggerGeneration();

  // Server captures the cover during compile/postprocess for both component
  // and legacy flows; read whatever's on the structure. If null, the cover
  // capture hasn't completed yet — proceed without a screenshot attachment.
  const ensureScreenshot = useCallback((): string | null => {
    if (!structureId) return null;
    if (!screenshotUrl) {
      toast.info('Screenshot not ready yet — feedback sent without image');
    }
    return screenshotUrl ?? null;
  }, [screenshotUrl, structureId]);

  // Handle like - captures screenshot if needed, sends to Slack
  const handleLike = useCallback(() => {
    if (!structureId || !screenId || activeAction !== null) return;
    setActiveAction('like');

    try {
      amplitudeService.logEvent(`[${analyticsSource}] feedback`, {
        chatId: screenId,
        structureId,
        like: true,
        flowId,
        hasScreenshot: !!screenshotUrl,
      });

      const finalScreenshotUrl = ensureScreenshot();
      sendFeedbackToSlackMutation.mutate({
        flowId,
        screenId,
        structureId,
        screenshotUrl: finalScreenshotUrl,
        type: 'like',
      });
    } finally {
      setActiveAction(null);
    }
  }, [
    activeAction,
    analyticsSource,
    ensureScreenshot,
    flowId,
    screenId,
    screenshotUrl,
    sendFeedbackToSlackMutation,
    structureId,
  ]);

  // Handle dislike - captures screenshot if needed, sends to Slack, opens modal
  const handleDislike = useCallback(() => {
    if (!structureId || !screenId || activeAction !== null) return;
    setActiveAction('dislike');

    try {
      amplitudeService.logEvent(`[${analyticsSource}] dislike clicked`, {
        chatId: screenId,
        structureId,
        flowId,
        hasScreenshot: !!screenshotUrl,
      });

      const finalScreenshotUrl = ensureScreenshot();
      sendFeedbackToSlackMutation.mutate({
        flowId,
        screenId,
        structureId,
        screenshotUrl: finalScreenshotUrl,
        type: 'dislike',
      });

      // Modal regeneration requires the screenshot — skip the modal otherwise.
      if (finalScreenshotUrl) setIsModalOpen(true);
    } finally {
      setActiveAction(null);
    }
  }, [
    activeAction,
    analyticsSource,
    ensureScreenshot,
    flowId,
    screenId,
    screenshotUrl,
    sendFeedbackToSlackMutation,
    structureId,
  ]);

  const closeModal = useCallback(() => {
    amplitudeService.logEvent(
      `[${analyticsSource}] dislike feedback modal cancelled`,
      {
        chatId: screenId,
        structureId,
        flowId,
      },
    );
    setIsModalOpen(false);
  }, [analyticsSource, flowId, screenId, structureId]);

  const handleSubmit = useCallback(
    (data: {categories: FeedbackCategory[]; freeText: string}) => {
      const finalScreenshotUrl = screenshotUrl;
      if (!structureId || !screenId || !finalScreenshotUrl) return;

      amplitudeService.logEvent(
        `[${analyticsSource}] dislike feedback submitted`,
        {
          flowId,
          screenId,
          structureId,
          categories: data.categories,
          hasFreeText: data.freeText.trim().length > 0,
        },
      );

      setIsModalOpen(false);

      const runRegeneration = async () => {
        try {
          await pushRegenerateMessageMutation.mutateAsync({
            screenId,
            structureId,
            categories: data.categories,
            freeText: data.freeText,
            screenshotUrl: finalScreenshotUrl,
          });
          triggerGeneration(flowId, screenId);
        } catch (error) {
          captureException(error);
        }
      };

      void runRegeneration();
    },
    [
      analyticsSource,
      flowId,
      pushRegenerateMessageMutation,
      screenId,
      screenshotUrl,
      structureId,
      triggerGeneration,
    ],
  );

  return {
    isModalOpen,
    activeAction,
    handleLike,
    handleDislike,
    closeModal,
    handleSubmit,
  };
};
